import { useEffect, useState } from 'react'

/**
 * Mirrors the `?tab=` search param into local state for the asset detail page.
 *
 * The tab is pushed with a raw `history.pushState` (AssetDetailPage) so that
 * switching tabs never remounts the — expensive — page. The cost of bypassing
 * the router that way is that nothing re-reads the URL on its own, so this hook
 * owns the two re-sync points:
 *
 *   - `assetUid` changes: the route is the same, so the component is not
 *     remounted and the previously-selected tab would linger onto the new asset
 *     (a building's "plan" tab carrying over to a site that has none).
 *   - `popstate`: browser back/forward moves the URL between two tabs of the
 *     *same* asset, which the `assetUid` effect cannot see. Without it the URL
 *     said `?tab=compliance` while the plan viewer — "Déposez un plan" — stayed
 *     on screen (issue #1017).
 *
 * AssetDetailPage additionally clamps an out-of-range tab to the asset's
 * default, so orphan content can never render even for a tab this hook restores.
 */
export function useUrlTab(assetUid: string, fallback?: string) {
    const readTab = () => new URLSearchParams(window.location.search).get('tab') || fallback

    const [activeTab, setActiveTab] = useState<string | undefined>(readTab)

    useEffect(() => {
        setActiveTab(readTab())
        // `readTab` is re-created every render; keying on the asset is the point.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetUid, fallback])

    useEffect(() => {
        const onPopState = () => setActiveTab(readTab())
        window.addEventListener('popstate', onPopState)
        return () => window.removeEventListener('popstate', onPopState)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fallback])

    return [activeTab, setActiveTab] as const
}
